import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';


import {ErrorFlatButton} from 'src/components/error-flat-button/error-flat-button';
import {RequiredMark} from 'src/components/required-mark';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {AccountTeamMemberSuggest} from 'src/modules/create-account/components/account-team-member-suggest';
import {NO_PRIMARY_ATM} from 'src/modules/create-account/constants';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {
    ACCOUNT_TEAM_FULL_MESSAGE,
} from 'src/modules/profile/account/tabs/account-profile/account-managers-section/constant';
import {getAccountManagerInfo} from 'src/openapi/admin/apis/AccountManagerRelationsApi';
import {useAppDispatch} from 'src/store/hooks';

import {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {RadioButton} from '@pb/kit-component-radio-button';

import {AccountTeamSkeleton} from './account-team-skeleton';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccountDetails, AccountTeamMember} from 'src/modules/create-account/typedef';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';

type Props = {
    accountId?: number,
    accountTeamMembers: Array<AccountTeamMember>,
    onLoad: (accountTeam: Array<AccountTeamMember>) => void,
    onChange: React.Dispatch<React.SetStateAction<AccountDetails>>,
    onFieldChange: (parameterName: string) => void,
};

export const AccountTeam: React.FC<Props> = props => {
    const {accountTeamMembers, onChange, accountId, onLoad, onFieldChange} = props;
    const apiDispatch = useAppDispatch();
    const {filteredAccountManagerTypes} = useAccountManagerTypes();
    const [loading, setLoading] = useState(false);

    const me = useSelector(selectUserShortInfo);
    const {removeError} = useContext<ValidationContextType>(ValidationContext);

    useEffect(() => {
        if (accountId) {
            const loadAccountTeam = async () => {
                setLoading(true);
                const accountTMResponse = await apiDispatch(getAccountManagerInfo({accountId}));
                if (accountTMResponse.payload.success) {
                    onLoad(accountTMResponse.payload.data.map(manager => ({
                        user: {
                            email: manager.email,
                            id: manager.id,
                            fullName: manager.fullName,
                            avatar: manager.avatar,
                        },
                        type: manager.managerType,
                        primary: manager.primary,
                        receiveAccountAlerts: manager.receiveAccountAlerts,
                    })));
                }
                setLoading(false);
            };
            loadAccountTeam();
        }
    }, [accountId, apiDispatch, onLoad]);

    useEffect(() => {
        accountTeamMembers.forEach(({primary, user}, id) => {
            if (primary && user) {
                removeError(`account-team-member-select-${id}`, [NO_PRIMARY_ATM]);
            }
        });

        if (accountTeamMembers.some(({user}) => user?.id === me?.userId)) {
            removeError('account-team');
        }
    }, [accountTeamMembers, me, removeError]);

    const onSetAccountManager = (id: number, selectedUser?: AccountManagerSearchResult) => {
        onFieldChange('Account Team Members: Select Member');
        onChange(details => ({
            ...details,
            accountTeamMembers: details.accountTeamMembers.map((teamMember, i) => ({
                ...teamMember,
                user: i === id ? selectedUser : teamMember.user,
            })),
        }));
    };

    const onSetRole = (id: number, role: string) => {
        onFieldChange('Account Team Members: Role');
        onChange(details => ({
            ...details,
            accountTeamMembers: details.accountTeamMembers.map((teamMember, i) => ({
                ...teamMember,
                type: i === id ? role : teamMember.type,
            })),
        }));
    };

    const onSetPrimary = (id: number) => {
        onFieldChange('Account Team Members: Primary');
        onChange(details => ({
            ...details,
            accountTeamMembers: details.accountTeamMembers.map((teamMember, i) => ({
                ...teamMember,
                primary: i === id,
                receiveAccountAlerts: i === id || teamMember.receiveAccountAlerts,
            })),
        }));
    };

    const onSetReceiveAlerts = (id: number, data: {event: React.SyntheticEvent<HTMLInputElement, Event>}) => {
        onFieldChange('Account Team Members: Receive Account Alerts');
        onChange(details => ({
            ...details,
            accountTeamMembers: details.accountTeamMembers.map((teamMember, i) => ({
                ...teamMember,
                receiveAccountAlerts: i === id ? data.event.currentTarget.checked : teamMember.receiveAccountAlerts,
            })),
        }));
    };

    const getAllowedTypes = useCallback((selectedType?: string) => {
        const types = accountTeamMembers.map(({type}) => type);
        return filteredAccountManagerTypes.filter(type => !types.includes(type.code) || (type.code === selectedType));
    }, [accountTeamMembers, filteredAccountManagerTypes]);

    const onAddAccountTeamMember = () => {
        onChange(details => ({
            ...details,
            accountTeamMembers: [
                ...details.accountTeamMembers,
                {
                    primary: false,
                    type: getAllowedTypes()[0].code,
                    receiveAccountAlerts: true,
                },
            ],
        }));
    };

    const onRemoveAccountTeamMember = (removeId: number) => {
        onChange(details => ({
            ...details,
            accountTeamMembers: details.accountTeamMembers.filter((member, i) => {
                if (i === removeId && member?.user) {
                    onFieldChange('Account Team Members: Remove Member');
                }

                return i !== removeId;
            }),
        }));
    };

    return (
        <Skeleton view={<AccountTeamSkeleton/>} shown={loading}>
            <ValidatedControl name="account-team">
                {() => (
                    <FormSection headline="Account Team">
                        <Group direction="v" gap="s" alignItems="start">
                            <Group gap="xl" alignItems="start">
                                <WidthItem width={430}>
                                    <Label><RequiredMark label="Member"/></Label>
                                </WidthItem>
                                <WidthItem width={315}>
                                    <Label>Role</Label>
                                </WidthItem>
                                <Label>Options</Label>
                            </Group>
                            {accountTeamMembers.map((manager, id) => (
                                <Group gap="xl" alignItems="start" key={id}>
                                    <ValidatedControl name={`account-team-member-select-${id}`}>
                                        {invalid => (
                                            <AccountTeamMemberSuggest
                                                id={`account-team-member-select-${id}`}
                                                selectedTeamMember={manager.user}
                                                selectedTeamMembers={accountTeamMembers.map(({user}) => user)}
                                                onSelect={user => onSetAccountManager(id, user)}
                                                placeholder="Account team member"
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                            />
                                        )}
                                    </ValidatedControl>
                                    <WidthItem width={315}>
                                        <ComboBox value={manager.type} onChange={role => onSetRole(id, role)}>
                                            {getAllowedTypes(manager.type).map(({code, description}) =>
                                                <ComboboxItem key={code} caption={description} value={code}/>,
                                            )}
                                        </ComboBox>
                                    </WidthItem>
                                    <Item className="group-item pdy-xs">
                                        <Group gap="xl">
                                            <RadioButton
                                                value={id}
                                                label="Primary"
                                                checked={manager.primary}
                                                onChange={onSetPrimary}
                                            />
                                            <CheckBox
                                                disabled={manager.primary}
                                                checked={manager.receiveAccountAlerts}
                                                label="Account Alerts"
                                                onCheckBoxChange={data => onSetReceiveAlerts(id, data)}
                                            />
                                            {!manager.primary && (
                                                <ErrorFlatButton
                                                    icon={Icons.TIMES_CIRCLE}
                                                    label="Remove"
                                                    onClick={() => onRemoveAccountTeamMember(id)}
                                                />
                                            )}
                                        </Group>
                                    </Item>
                                </Group>
                            ))}
                            <Group direction="h">
                                <FlatButton
                                    disabled={
                                        accountTeamMembers.length === filteredAccountManagerTypes.length ||
                                        accountTeamMembers.some(({user}) => !user) ||
                                        getAllowedTypes().length === 0
                                    }
                                    startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                                    onClick={onAddAccountTeamMember}>
                                    <b>Add Account Team Member</b>
                                </FlatButton>
                                {getAllowedTypes().length === 0
                                    ? <Info type={{popover: ACCOUNT_TEAM_FULL_MESSAGE}}/>
                                    : null}
                            </Group>
                        </Group>
                    </FormSection>
                )}
            </ValidatedControl>
        </Skeleton>
    );
};
